// src/pages/ChatBot/configs/sections.ts
//
// PRESENTATION manifest for the Configs tab — a UI-only reorganisation of the
// registry-driven config surface into OUTCOME themes an admin actually thinks in:
// Answers, Search, Documents, Saved answers. The backend registry, stages, and
// config ids are UNCHANGED; this file only decides which config id appears under
// which theme, in which tier, and in what order.
//
// Each theme splits its settings into two ordered tiers:
//   basic    — the few simple, low-risk knobs, shown first;
//   advanced — everything else (thresholds, counts, rebuild/locked/coming-soon
//              fields), tucked under a collapsible "Advanced settings" disclosure.
// The two lists give display order for free, and the panel renders basic then
// advanced. Ids are placed by the rule: `free` + conceptually simple → basic;
// thresholds/counts/expert knobs + every rebuild/locked/feature-flagged field →
// advanced. (One deliberate exception: the two high-level document choices —
// chunking strategy and contextual retrieval — stay basic despite being
// rebuild-class, so the Documents pane never opens onto an empty basic section.)
//
// Any surface entry whose id is NOT referenced here is appended to a per-stage
// theme's ADVANCED tier (see FALLBACK_CHILD_BY_STAGE), so a future registry def
// still renders without a manifest edit.

import type { Stage } from './types';

export interface PresentationTheme {
  // Stable selection key (the panel's active-theme state is a theme key).
  key: string;
  label: string;
  blurb: string;
  // Config ids to render, in display order, per tier. Ids absent from the surface
  // are skipped; surface entries not named here are appended per-stage to advanced.
  basic: string[];
  advanced: string[];
  // Render the presentation-only "Reranking enabled" toggle at the top of the
  // advanced block, bound to postRetrieval.reranker (on == value !== 'none').
  // Pure sugar over that id — the reranker variant + its knobs live in advanced.
  rerankToggle?: boolean;
}

// Ordered themes (easy → advanced). Sidebar and detail read this directly.
export const CONFIG_SECTIONS: PresentationTheme[] = [
  {
    key: 'answers',
    label: 'Answers',
    blurb: 'How the chatbot writes its replies — the detail, length, and tone.',
    basic: [
      'answerGen.style',
      'answerGen.qualityTier',
      'answerGen.maxAnswerTokens',
      'answerGen.temperature',
      'answerGen.abstainString',
    ],
    advanced: ['postRetrieval.finalTopK', 'postRetrieval.contextTokenBudget'],
  },
  {
    key: 'search',
    label: 'Search',
    blurb: 'How the chatbot finds the right passages in your documents to answer from.',
    rerankToggle: true,
    basic: ['retrieval.approach', 'postRetrieval.vectorWeight'],
    // Order groups the advanced knobs: how much to fetch and how strict to be,
    // then how the question is handled, then how the two result lists are merged,
    // then reranking (its enable toggle renders atop this block).
    advanced: [
      'retrieval.vectorCandidates',
      'retrieval.minVectorScore',
      'retrieval.textCandidates',
      'retrieval.maxFullTextTerms',
      'retrieval.minTextMatchedTerms',
      'queryPrep.maxQueryLength',
      'queryPrep.expansion',
      'queryPrep.decomposition',
      'retrieval.multiQueryStrategy',
      'retrieval.contextExpansion',
      'postRetrieval.fusion',
      'postRetrieval.adaptiveAlphaMin',
      'postRetrieval.adaptiveAlphaMax',
      'postRetrieval.rrfK',
      'postRetrieval.fusedTopK',
      'postRetrieval.minFusedScore',
      'postRetrieval.reranker',
      'postRetrieval.rerankCandidates',
      'postRetrieval.minRerankScore',
      'postRetrieval.rerankMaxPassageChars',
    ],
  },
  {
    key: 'documents',
    label: 'Documents',
    blurb:
      'How your uploaded documents are prepared for search. Changing these settings re-processes your documents.',
    basic: ['chunking.strategy', 'chunking.contextualRetrieval'],
    advanced: [
      'chunking.minChunkTokens',
      'chunking.maxChunkTokens',
      'chunking.overlapRatio',
      'chunking.fixed.maxTokens',
      'chunking.fixed.overlapRatio',
      'chunking.semantic.maxTokens',
      'chunking.semantic.breakpointPercentile',
      'chunking.semantic.minSentencesPerChunk',
      'embedding.model',
      'embedding.dimensions',
    ],
  },
  {
    key: 'savedAnswers',
    label: 'Saved answers',
    blurb: 'When an answer you have saved is reused to reply to a similar new question.',
    basic: ['queryLogs.overrideSimilarityThreshold'],
    advanced: ['queryLogs.overrideTermCoverage', 'queryLogs.overrideKeywordVectorGuard'],
  },
];

// The config id bound to the presentation-level "Reranking enabled" toggle, and
// the tag toggling ON should stage when the surface default is itself 'none'.
export const RERANKER_ID = 'postRetrieval.reranker';
export const RERANKER_OFF_TAG = 'none';
export const RERANKER_ON_FALLBACK_TAG = 'llm-judge';

// The keyword↔meaning balance slider is runtime-ignored while the fusion strategy
// is the adaptive balance (which sets the weights per query) — the panel greys the
// balance row whenever the fusion variant's effective value is the adaptive tag.
export const FUSION_ID = 'postRetrieval.fusion';
export const FUSION_ADAPTIVE_TAG = 'adaptive-rrf';
export const FUSION_BALANCE_ID = 'postRetrieval.vectorWeight';

// Where an unreferenced surface entry lands, keyed by its stage — appended to that
// theme's ADVANCED tier so future registry defs still render, tucked out of the way.
export const FALLBACK_CHILD_BY_STAGE: Record<Stage, string> = {
  chunking: 'documents',
  embedding: 'documents',
  queryPrep: 'search',
  retrieval: 'search',
  postRetrieval: 'search',
  answerGen: 'answers',
  queryLogs: 'savedAnswers',
};

// ── Derived, surface-independent indexes (computed once at module load) ──────

// Every config id a theme names, in tier order.
export const themeIds = (theme: PresentationTheme): string[] => [
  ...theme.basic,
  ...theme.advanced,
];

export function findTheme(key: string): PresentationTheme | undefined {
  return CONFIG_SECTIONS.find((theme) => theme.key === key);
}

// Every config id named anywhere in the manifest — the complement is what gets
// appended to a fallback theme's advanced tier.
export const REFERENCED_IDS: ReadonlySet<string> = new Set(
  CONFIG_SECTIONS.flatMap(themeIds),
);
